import _ from 'lodash';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {randomString} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {AccountAttributeUpdateAction} from '../../../../helpers/models/accountAttributeUpdateHelpers';
import {ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {CardModel} from '../../../../models/api/cardModel';
import {ConversationBaseModel, ConversationModel} from '../../../../models/api/conversationModel';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {LegacyApplicationModel} from '../../../../models/api/legacyApplicationModel';
import {ThunkAction} from '../../../appThunk';
import {CellsContext} from '../../cells/context/cellsContext';
import {PanelActionPayload} from '../../panelsActions';
import {makeGetAppPanelIdFromPanel} from '../../panelsSelectors';
import {AppPanelId, ContactDetailsPanelId, NewPanelId} from '../../panelStateBase';
import {
  makeGetErrorBannersForPanel,
  makeGetUndoBannersForPanel,
  makeGetVisibleBannersForPanel,
} from './bannersSelectors';
import {
  AccountDetailsUndoBannerState,
  AccountsErrorBannerState,
  AccountsErrorBannerTypesEnum,
  AccountsInfoBannerState,
  AccountsInfoBannerTypesEnum,
  ApplicationBannerState,
  ApplicationBannerTypesEnum,
  BannerState,
  BannerTypesEnum,
  ContactDetailsErrorBannerState,
  ContactDetailsErrorTypesEnum,
  ContactDetailsUndoBannerState,
  ContactsManagerErrorBannerState,
  ContactsManagerErrorBannerTypesEnum,
  ContactsManagerSuccessBannerState,
  ContactsManagerSuccessBannerTypesEnum,
  ConversationUndoBannerState,
  ConversationUndoBannerTypesEnum,
  ErrorBannerTypesEnum,
  InfoBannerTypesEnum,
  MessageUndoBannerState,
  MessageUndoBannerTypesEnum,
  SuccessBannerTypesEnum,
  UndoBannerTypesEnum,
} from './bannersState';
import {ContactDetailsUndoBannerAction} from './contactDetailsUndoBannersActions';

/*
 * Constants.
 */

export const bannerUndoDelay = 8 * 1000; // 8 seconds.
const bannerSuccessDelay = 8 * 1000; // 8 seconds.
const bannerErrorDelay = 8 * 1000; // 8 seconds.

/*
 * Type enum.
 */

export enum BannersActionsEnum {
  UPDATE_BANNERS = '@@panels/app/UPDATED_BANNERS',
}

/*
 * Types.
 */

interface UpdateBannersContext extends PanelActionPayload<AppPanelId> {
  bannersToAdd?: ReadonlyArray<BannerState>;
  visibleBannerUidsToRemove?: ReadonlyArray<string>;
  undoBannerUidsToRemove?: ReadonlyArray<string>;
  errorBannerUidsToRemove?: ReadonlyArray<string>;
}
interface UpdateBannersAction extends ActionWithPayload<UpdateBannersContext> {
  type: BannersActionsEnum.UPDATE_BANNERS;
}

/*
 * Action creators.
 */

export function showMessageUndoBannerAction(
  panelId: AppPanelId,
  messageUid: string,
  messageUndoType: MessageUndoBannerTypesEnum,
  expiresAt: number,
): ThunkAction<Handler> {
  return (dispatch) => {
    // Create the new banner.
    const banner: MessageUndoBannerState = {
      type: BannerTypesEnum.UNDO,
      undoType: UndoBannerTypesEnum.MESSAGE,
      uid: makeBannerUid(),
      messageUid,
      messageUndoType,
      createdAt: Date.now(),
      expiresAt,
    };

    // After a given delay, remove the banner.
    dispatch(queueRemoveExpiredBannersAction(panelId, expiresAt - Date.now()));

    // Wait 400ms for performance reasons.
    const delay = delayAsync(400);
    delay.then(() => dispatch(addBannerAction(panelId, banner)));

    // Return a method to hide the banner.
    return () => {
      delay.then(() => dispatch(removeBannerAction(panelId, banner.uid)));
    };
  };
}

export function showConversationUndoBannerAction(
  panelId: AppPanelId,
  cellsContext: CellsContext,
  conversationUndoType: ConversationUndoBannerTypesEnum,
  oldConversations: ReadonlyArray<ConversationBaseModel>,
  newConversations: ReadonlyArray<ConversationBaseModel>,
  onlyOldConversation: ConversationModel | undefined,
): ThunkAction {
  return (dispatch) => {
    // Create the new banner.
    const now = Date.now();
    const banner: ConversationUndoBannerState = {
      type: BannerTypesEnum.UNDO,
      undoType: UndoBannerTypesEnum.CONVERSATION,
      uid: makeBannerUid(),
      conversationUndoType,
      oldConversations,
      newConversations,
      onlyOldConversation,
      cellsContext,
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
    };

    dispatch(showBannerWithAutomaticRemovalAction(panelId, banner));
  };
}

export function showContactDetailsUndoBannerAction(
  panelId: ContactDetailsPanelId,
  cardId: number,
  action: ContactDetailsUndoBannerAction,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ContactDetailsUndoBannerState = {
      type: BannerTypesEnum.UNDO,
      undoType: UndoBannerTypesEnum.CONTACT_DETAILS,
      uid: makeBannerUid(),
      panelId,
      cardId,
      action,
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showContactDetailsErrorBannerAction(
  panelId: NewPanelId,
  contactDetailsErrorType: ContactDetailsErrorTypesEnum,
  // TODO: Type this more strongly: this is only applicable when errorType is HANDLE_TAKEN.
  params?: {
    mainCard?: CardModel;
    conflictingContactUid?: string;
    conflictingCard?: CardModel;
    contactDetailsPanelId?: ContactDetailsPanelId;
  },
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ContactDetailsErrorBannerState = {
      type: BannerTypesEnum.ERROR,
      errorType: ErrorBannerTypesEnum.CONTACT_DETAILS,
      contactDetailsErrorType,
      uid: makeBannerUid(),
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
      params,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showAccountDetailsUndoBannerAction(
  panelId: NewPanelId,
  accountId: number,
  action: AccountAttributeUpdateAction,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: AccountDetailsUndoBannerState = {
      type: BannerTypesEnum.UNDO,
      uid: makeBannerUid(),
      undoType: UndoBannerTypesEnum.ACCOUNT_DETAILS,
      accountId,
      action,
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showAccountsErrorBannerAction(
  panelId: NewPanelId,
  accountsErrorType: AccountsErrorBannerTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: AccountsErrorBannerState = {
      type: BannerTypesEnum.ERROR,
      errorType: ErrorBannerTypesEnum.ACCOUNTS,
      uid: makeBannerUid(),
      accountsErrorType,
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showAccountsInfoBannerAction(
  panelId: NewPanelId,
  accountsInfoType: AccountsInfoBannerTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: AccountsInfoBannerState = {
      type: BannerTypesEnum.INFO,
      infoType: InfoBannerTypesEnum.ACCOUNTS,
      accountsInfoType,
      uid: makeBannerUid(),
      createdAt: now,
      expiresAt: now + bannerUndoDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showContactsManagerErrorBannerAction(
  panelId: NewPanelId,
  contactsManagerErrorType: ContactsManagerErrorBannerTypesEnum,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ContactsManagerErrorBannerState = {
      type: BannerTypesEnum.ERROR,
      errorType: ErrorBannerTypesEnum.CONTACTS_MANAGER,
      contactsManagerErrorType,
      uid: makeBannerUid(),
      createdAt: now,
      expiresAt: now + bannerErrorDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showContactsManagerCardsMergedSuccessBannerAction(
  panelId: NewPanelId,
  totalCards: number,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ContactsManagerSuccessBannerState = {
      type: BannerTypesEnum.SUCCESS,
      successType: SuccessBannerTypesEnum.CONTACTS_MANAGER,
      contactsManagerSuccessType: ContactsManagerSuccessBannerTypesEnum.CARDS_MERGED,
      uid: makeBannerUid(),
      createdAt: now,
      expiresAt: now + bannerSuccessDelay,
      totalCards,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showContactsManagerContactCreatedSuccessBannerAction(panelId: NewPanelId): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ContactsManagerSuccessBannerState = {
      type: BannerTypesEnum.SUCCESS,
      successType: SuccessBannerTypesEnum.CONTACTS_MANAGER,
      contactsManagerSuccessType: ContactsManagerSuccessBannerTypesEnum.CONTACT_CREATED,
      uid: makeBannerUid(),
      createdAt: now,
      expiresAt: now + bannerSuccessDelay,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function showApplicationEnabledBannerAction(
  panelId: NewPanelId,
  integration?: IntegrationModel,
  application?: LegacyApplicationModel,
): ThunkAction {
  return (dispatch, getState) => {
    const now = Date.now();
    const banner: ApplicationBannerState = {
      type: BannerTypesEnum.INFO,
      infoType: InfoBannerTypesEnum.APPLICATIONS,
      applicationType: ApplicationBannerTypesEnum.APPLICATION_ENABLED,
      uid: makeBannerUid(),
      createdAt: now,
      integrationId: integration?.id,
      legacyApplicationId: application?.id,
    };

    const appPanelId = makeGetAppPanelIdFromPanel()(getState(), {panelId});
    dispatch(showBannerWithAutomaticRemovalAction(appPanelId, banner));
  };
}

export function hideBannerAction(panelId: AppPanelId, bannerUid: string): UpdateBannersAction {
  const payload: UpdateBannersContext = {
    panelId,
    visibleBannerUidsToRemove: [bannerUid],
  };

  return {
    type: BannersActionsEnum.UPDATE_BANNERS,
    payload,
  };
}

export function removeBannerAction(panelId: AppPanelId, bannerUid: string): UpdateBannersAction {
  const payload: UpdateBannersContext = {
    panelId,
    visibleBannerUidsToRemove: [bannerUid],
    undoBannerUidsToRemove: [bannerUid],
  };

  return {
    type: BannersActionsEnum.UPDATE_BANNERS,
    payload,
  };
}

/*
 * Helpers.
 */

function showBannerWithAutomaticRemovalAction(panelId: AppPanelId, banner: BannerState): ThunkAction {
  return (dispatch) => {
    // Wait 400ms for performance reasons.
    setTimeout(() => dispatch(addBannerAction(panelId, banner)), 400);

    // After a given delay, remove the banner.
    dispatch(queueRemoveExpiredBannersAction(panelId, bannerUndoDelay));
  };
}

function addBannerAction(panelId: AppPanelId, banner: BannerState): ThunkAction {
  return (dispatch, getState) => {
    const visibleBanners = makeGetVisibleBannersForPanel()(getState(), {panelId});
    const undoBanners = makeGetUndoBannersForPanel()(getState(), {panelId});
    const errorBanners = makeGetErrorBannersForPanel()(getState(), {panelId});

    const payload: UpdateBannersContext = {
      panelId,
      bannersToAdd: [banner],
      visibleBannerUidsToRemove: findBannerUidsToRemove(visibleBanners, banner, 2),
      undoBannerUidsToRemove: findBannerUidsToRemove(undoBanners, banner, 30),
      errorBannerUidsToRemove: findBannerUidsToRemove(errorBanners, banner, 30),
    };

    const action: UpdateBannersAction = {
      type: BannersActionsEnum.UPDATE_BANNERS,
      payload,
    };

    dispatch(action);
  };
}

function findBannerUidsToRemove(
  banners: ReadonlyArray<BannerState>,
  newBanner: BannerState,
  numberToKeep: number,
) {
  if (newBanner.type !== BannerTypesEnum.UNDO || newBanner.undoType !== UndoBannerTypesEnum.CONVERSATION) {
    return [];
  }

  const conversationBanners = _(banners)
    .map((b) => b.type === BannerTypesEnum.UNDO && b.undoType === UndoBannerTypesEnum.CONVERSATION && b)
    .compact()
    .value();

  // Find the banners that we ultimately want undoable.
  const conversationBannersToKeep = _(conversationBanners)
    .filter((b) => isUndoableConversationBanner(b, newBanner))
    .takeRight(numberToKeep)
    .value();

  // Diff the original collection to find the IDs to remove.
  return _(conversationBanners)
    .difference(conversationBannersToKeep)
    .map((b) => b.uid)
    .value();
}

function isUndoableConversationBanner(
  banner: ConversationUndoBannerState,
  newBanner: ConversationUndoBannerState,
) {
  return (
    newBanner.newConversations.length !== 1 ||
    banner.newConversations.length !== 1 ||
    newBanner.newConversations[0].id !== banner.newConversations[0].id
  );
}

function queueRemoveExpiredBannersAction(panelId: AppPanelId, delay: number): ThunkAction {
  return (dispatch) => {
    // We add a safety delay to make sure we're past the expiration date.
    setTimeout(() => dispatch(hideExpiredBannersAction(panelId)), delay + 10);
  };
}

function hideExpiredBannersAction(panelId: AppPanelId): ThunkAction {
  return (dispatch, getState) => {
    // Find expired banners for this panel.
    const now = Date.now();
    const visibleBanners = makeGetVisibleBannersForPanel()(getState(), {panelId});
    const expiredBanners = visibleBanners.filter((b) => b.expiresAt && b.expiresAt <= now);
    const notUndoableExpiredBanners = expiredBanners.filter(
      (b) => b.type === BannerTypesEnum.UNDO && b.undoType === UndoBannerTypesEnum.MESSAGE,
    );
    const errorExpiredBanners = expiredBanners.filter((b) => b.type === BannerTypesEnum.ERROR);

    // We may not have anything to do.
    if (expiredBanners.length === 0) {
      return;
    }

    // Otherwise, remove the banners.
    const payload: UpdateBannersContext = {
      panelId,
      visibleBannerUidsToRemove: expiredBanners.map((b) => b.uid),
      undoBannerUidsToRemove: notUndoableExpiredBanners.map((b) => b.uid),
      errorBannerUidsToRemove: errorExpiredBanners.map((b) => b.uid),
    };

    const action: UpdateBannersAction = {
      type: BannersActionsEnum.UPDATE_BANNERS,
      payload,
    };

    dispatch(action);
  };
}

function makeBannerUid() {
  return randomString();
}

/*
 * Combined action type.
 */

export type BannersAction = UpdateBannersAction;
